// @ts-check

/** @file Status-first parsing for AwesomeTasks project sync HTTP responses. */

import {ProjectSyncTransportError} from "./project-sync-error.js"

/**
 * Classifies retryable HTTP outages before touching an optional or non-JSON body.
 * Successful and permanent-error responses retain fail-loud JSON parsing.
 * @param {{json: () => Promise<unknown> | unknown, ok: boolean, status: number}} response Fetch-compatible response.
 * @returns {Promise<unknown>} Parsed response payload.
 * @rejects {ProjectSyncTransportError | SyntaxError} Safe HTTP failure or malformed JSON contract.
 */
export async function parseProjectSyncHttpResponse(response) {
  const retryable = response.status === 408 || response.status === 429 || response.status >= 500

  // Classify every non-ok response before touching its body: an error page or
  // empty error body must never escape as a raw JSON SyntaxError, which the
  // coordinator would classify as an unclassified `sync-failed`.
  if (!response.ok) {
    throw new ProjectSyncTransportError({code: `http-${response.status}`, retryable, status: response.status})
  }

  let payload

  try {
    payload = await response.json()
  } catch (error) {
    // A malformed body stays a contract failure. A body the browser could not
    // deliver (navigation, connection reset) is a transport failure: left raw,
    // it would publish a permanent, non-retryable `sync-failed`.
    if (error instanceof SyntaxError) throw error
    throw new ProjectSyncTransportError({code: "network-unavailable", retryable: true})
  }

  const payloadRecord = payload && typeof payload === "object" && !Array.isArray(payload)
    ? /** @type {Record<string, unknown>} */ (payload)
    : null
  if (payloadRecord?.status === "error") {
    throw new ProjectSyncTransportError({code: safeErrorCode(payloadRecord.errorCode), retryable: false})
  }

  return payload
}

/**
 * Reduces a server error code to safe stable metadata.
 * @param {unknown} value Untrusted server code.
 * @returns {string} Safe bounded code.
 */
function safeErrorCode(value) {
  return typeof value === "string" && /^[a-z0-9-]{1,80}$/u.test(value) ? value : "sync-request-rejected"
}
